import React, { ReactNode } from 'react'

import BuildConfig from '$extensionSrc/buildConfig'
import { SettingKey } from '$extensionSrc/recommendations/recommendationSettingsKeys'

import SectionNotFoundException from './exception/sectionNotFoundException'
import SettingNotFoundException from './exception/settingNotFoundException'
import SidebarLink from '../components/SidebarLink'
import { unavailableSettingFaqUrl } from '../consts/urlConsts'

export function getCurrentValueFnFromKey(
  key: string,
  formatFn: (value: any) => string,
): (initialValues: { [key: string]: any }) => string {
  return (initialValues: { [key: string]: any }) => {
    const currentValue = initialValues?.[key]

    if (currentValue === null || currentValue === undefined) {
      return 'Unknown error'
    }

    return formatFn(currentValue)
  }
}

export function getIsFulfilledFnFromKey(
  key: string,
  isFullfilledFn: (value: any) => boolean,
): (initialValues: { [key: string]: any }) => boolean {
  return (initialValues: { [key: string]: any }) => {
    const currentValue = initialValues?.[key]

    if (currentValue === null || currentValue === undefined) {
      return false
    }

    return isFullfilledFn(currentValue)
  }
}

export function getCantUndoFnFromKey(
  key: string,
  cantUndoFn: (value: any) => boolean,
): (initialValues: { [key: string]: any }) => boolean {
  return (initialValues: { [key: string]: any }) => {
    const currentValue = initialValues?.[key]

    if (currentValue === null || currentValue === undefined) {
      return true
    }

    return cantUndoFn(currentValue)
  }
}

export type ErrorData = {
  message: string
  name: string
}

export type AutomationErrors = {
  gatherError?: ErrorData
  updateError?: ErrorData
  /**
   * Legacy implementation
   */
  message?: string
}

export function getErrorIsDisabledAndReasonFn(
  settingsKey: string,
  reason?: string,
): (automationErrors: Partial<Record<SettingKey, AutomationErrors>>) => {
  isDisabled: boolean
  isDisabledReason: ReactNode | null
} {
  return (automationErrors) => {
    const error = automationErrors?.[settingsKey]?.gatherError
    if (error) {
      if (error.name === SectionNotFoundException.exceptionName) {
        return {
          isDisabled: true,
          isDisabledReason: (
            <span>
              Your account does not have this setting{' '}
              <SidebarLink href={unavailableSettingFaqUrl}>
                [learn more]
              </SidebarLink>
            </span>
          ),
        }
      } else if (error.name === SettingNotFoundException.exceptionName) {
        return {
          isDisabled: true,
          isDisabledReason: reason ? (
            <span>{reason}</span>
          ) : (
            <span>
              {BuildConfig.TENANT_NAME} couldn't find your current setting
            </span>
          ),
        }
      }
      return {
        isDisabled: true,
        isDisabledReason: 'Unknown error',
      }
    }
    return {
      isDisabled: false,
      isDisabledReason: null,
    }
  }
}

export const defaultCantUndoFn = () => false
export const defaultIsDisabledFn = () => false
export const defaultIsDisabledReasonFn = () => null
